import type { JSX } from 'react';

import { StrictMode, Suspense, lazy } from 'react';

import { createRoot } from 'react-dom/client';

import { LoginGate } from './components/LoginGate';

import { MasterPeekHost } from './components/MasterPeekHost';

import { registerServiceWorker } from './lib/pwa';

import { useRouteHref } from './lib/router';

import './styles/global.css';

import './styles/mobile-command-deck.css';

import './styles/workspace.css';

import './styles/product-experience.css';

import { OwnerConversationAccess } from './components/OwnerConversationAccess';

import { ProductNavigation } from './components/ProductNavigation';

import { PersonalChatPage } from './components/PersonalChatPage';

import { NativeAppBridge } from './components/NativeAppBridge';

import { ProductPage } from './components/ProductPage';
const OwnerConversationPage = lazy(() => import('./components/OwnerConversationPage').then(m => ({ default: m.OwnerConversationPage })));
const OperationsPage = lazy(() => import('./components/operations/OperationsPage').then(m => ({ default: m.OperationsPage })));
const UsagePage = lazy(() => import('./components/usage/UsagePage').then(m => ({ default: m.UsagePage })));
const FluidezBench = lazy(() => import('./components/FluidezBench').then((m) => ({ default: m.FluidezBench })));

// M12-FEAT PWA: registra o service worker (app instalavel + Web Push). Falha graciosa.

registerServiceWorker();

const App = lazy(() => import('./App').then((m) => ({ default: m.App })));

// Code-split: supervisão operacional carrega xterm; a conversa pessoal não precisa do deck.

const ChatPage = lazy(() => import('./components/ChatPage').then((m) => ({ default: m.ChatPage })));

// M315.6 — backlog do produto (read-only), rota dedicada como /evidence e /approvals.

const BacklogPage = lazy(() =>
  import('./components/BacklogPage').then((m) => ({ default: m.BacklogPage })),
);

// FASE 116 (116.D.7) — modo master do /chat (espelho + input do master orchestrator). Mesmo chunk pesado.


// FASE 158 (158.5) — superficie de revisao/aprovacao de EVIDENCIAS (rota dedicada `/evidence`).

const EvidenceReviewPage = lazy(() =>
  import('./components/EvidenceReviewPage').then((m) => ({ default: m.EvidenceReviewPage })),
);

// FASE 165 (165.1.g) — superficie de aprovacao de DRAFTS DE COMUNICACAO (rota dedicada `/approvals`).

const CommunicationDraftReviewPage = lazy(() =>
  import('./components/CommunicationDraftReviewPage').then((m) => ({
    default: m.CommunicationDraftReviewPage,
  })),
);

// FASE 167 (167.0.4) — CENTRAL DE COLABORACAO Master<->Usuario (rota dedicada `/central`, read-only v0).

const CentralPage = lazy(() => import('./components/CentralPage').then((m) => ({ default: m.CentralPage })));

// AL.E4 (AUD.24) — /health reúne observabilidade, execuções, instruções e dispositivos; as rotas antigas redirecionam.
const SystemHealthPage = lazy(() => import('./components/SystemHealthPage').then((m) => ({ default: m.SystemHealthPage })));




// M487.1 — espaço de trabalho por projeto (frentes/worktrees com escopo).

const WorkspacePage = lazy(() =>
  import('./components/WorkspacePage').then((module) => ({ default: module.WorkspacePage })),
);

// ECO.19 — segundo cérebro.
const SecondBrainPage = lazy(() => import('./components/SecondBrainPage').then((m) => ({ default: m.SecondBrainPage })));
const AppInstallPage = lazy(() => import('./components/AppInstallPage').then((m) => ({ default: m.AppInstallPage })));
const ExecutivePage = lazy(() => import('./components/ExecutivePage').then((m) => ({ default: m.ExecutivePage })));
// 30/10/2026 (12cb3b25) — painel de apontamentos do dono, no lugar do Artifact do Claude.
const OwnerPointsBoardPage = lazy(() => import('./components/OwnerPointsBoardPage').then((m) => ({ default: m.OwnerPointsBoardPage })));
const NovidadesPage = lazy(() => import('./components/NovidadesPage').then((m) => ({ default: m.NovidadesPage })));
// 28/09/2026 — reinício preparado do computador.
const RestartPrepPage = lazy(() => import('./components/RestartPrepPage').then((m) => ({ default: m.RestartPrepPage })));

// ECO.12-S — sessões por projeto e correio entre sessões.
const SessionsPage = lazy(() => import('./components/SessionsPage').then((m) => ({ default: m.SessionsPage })));

const AutonomyMatrixPage = lazy(() =>
  import('./components/AutonomyMatrixPage').then((module) => ({
    default: module.AutonomyMatrixPage,
  })),
);

const root = document.getElementById('root');

if (!root) {
  throw new Error('root element not found');
}

/**

 * M491 — conversa pessoal em / e /chat; projetos em /deck, workbench em /product.

 * AL.E4: o MASTER operacional vive em /central?tab=master (/chat?master=1 redireciona); /orchestrator conserva o chat
 * operacional legado; /health reúne as quatro telas de operação (rotas antigas redirecionam).

 * Links /?focus=<id> continuam abrindo o terminal. Navegação não abre sessões.

 */

function Root(): JSX.Element {
  const href = useRouteHref();

  const url = new URL(href, window.location.origin);

  const pathname = url.pathname.replace(/\/+$/, '');

  const isChat = pathname === '/chat';

  const isMaster = isChat && url.searchParams.get('master') === '1';

  const personalChat = (isChat && !isMaster) || (pathname === '' && !url.searchParams.has('focus'));

  const isEvidence = pathname === '/evidence';

  const isApprovals = pathname === '/approvals';

  // AL.E4 — /chat?master=1 vira /central?tab=master (a página redireciona com replace).
  const isCentral = pathname === '/central' || isMaster;

  const legacyHealth = ['/observability', '/runs', '/instructions', '/devices'].includes(pathname) ? pathname : undefined;
  const isHealth = pathname === '/health' || legacyHealth !== undefined;

  const isBacklog = pathname === '/backlog';

  const isAutonomy = pathname === '/autonomy';

  const isWorkspace = pathname === '/workspace';

  const isSessions = pathname === '/sessoes';

  const isBrain = pathname === '/cerebro';

  const isAppInstall = pathname === '/app';

  const isNovidades = pathname === '/novidades';

  // 02/10/2026 (8c2a8862) — Hoje: visão executiva do dono.
  const isHoje = pathname === '/hoje';

  const isApontamentos = pathname === '/apontamentos';

  const isRestartPrep = pathname === '/reiniciar';

  return (
    <LoginGate>
      <ProductNavigation />
      <NativeAppBridge />

      <div className="product-content">
        {(personalChat || isHoje) && <OwnerConversationAccess />}
        {pathname === '/donizeti' ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando conversa…</div>}><OwnerConversationPage /></Suspense>
        ) : personalChat ? (
          <PersonalChatPage />
        ) : pathname === '/bench/fluidez' ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando…</div>}><FluidezBench /></Suspense>
        ) : pathname === '/product' ? (
          <ProductPage />
        ) : pathname === '/operations' ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando operações…</div>}><OperationsPage /></Suspense>
        ) : pathname === '/consumo' ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando consumo…</div>}><UsagePage /></Suspense>
        ) : (isChat && !isMaster) || pathname === '/orchestrator' ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando chat…</div>}>
            <ChatPage />
          </Suspense>
        ) : isEvidence ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando…</div>}>
            <EvidenceReviewPage />
          </Suspense>
        ) : isApprovals ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando…</div>}>
            <CommunicationDraftReviewPage />
          </Suspense>
        ) : isCentral ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando…</div>}>
            <CentralPage redirectFrom={isMaster ? '/chat' : undefined} />
          </Suspense>
        ) : isHealth ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando saúde do sistema…</div>}>
            <SystemHealthPage redirectFrom={legacyHealth} />
          </Suspense>
        ) : isBacklog ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando backlog…</div>}>
            <BacklogPage />
          </Suspense>
        ) : isAppInstall ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando…</div>}>
            <AppInstallPage />
          </Suspense>
        ) : isRestartPrep ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando…</div>}>
            <RestartPrepPage />
          </Suspense>
        ) : isHoje ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando o dia…</div>}>
            <ExecutivePage />
          </Suspense>
        ) : isApontamentos ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando apontamentos…</div>}>
            <OwnerPointsBoardPage />
          </Suspense>
        ) : isNovidades ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando…</div>}>
            <NovidadesPage />
          </Suspense>
        ) : isBrain ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando o segundo cérebro…</div>}>
            <SecondBrainPage />
          </Suspense>
        ) : isSessions ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando sessões…</div>}>
            <SessionsPage />
          </Suspense>
        ) : isWorkspace ? (
          <Suspense fallback={<div className="chat-center chat-muted">carregando frentes…</div>}>
            <WorkspacePage />
          </Suspense>
        ) : isAutonomy ? (
          <Suspense
            fallback={<div className="chat-center chat-muted">carregando matriz de autonomia…</div>}
          >
            <AutonomyMatrixPage />
          </Suspense>
        ) : (
          <Suspense fallback={<div className="chat-center chat-muted">carregando projetos…</div>}>
            <App />
          </Suspense>
        )}

        <MasterPeekHost />
      </div>
    </LoginGate>
  );
}

createRoot(root).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
